import type { BoardProps } from 'boardgame.io/react';
import { useEffect, useLayoutEffect, useContext, useState, useRef, useMemo, createContext } from 'react';
import {
  DndContext,
  useDraggable,
  useDroppable,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { trackEvent } from '../utils/telemetry';
import type { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { BotContext, RunMatchContext, TutorialContext } from '../App';
import type { GrandSnatchState, CardData, Suit } from '../game/types';
import { validateDeckComposition } from '../game/deck';
import { SUITS, SUIT_SLUG } from '../game/types';
import { calculateLocationWinner } from '../game/locations';
import { decideWinner } from '../game/scoring';
import { PlayerPanel } from './PlayerPanel';
import { LocationBanner } from './LocationBanner';
import { PointsBadge } from './PointsBadge';
import { cardStateBadges, describeEffect } from './effectHelp';
import { TutorialOverlay } from './TutorialOverlay';
import { SERVER_ORIGIN } from '../config';
import './Board.css';

interface GrandSnatchBoardProps extends BoardProps<GrandSnatchState> { }

// --- Card animation context ---
// Refs to the deck and discard placeholders for each player. Used by Card to
// compute fly-in source (deck) and fly-out destination (discard) coordinates
// at mount/unmount time. When the context isn't provided (e.g. discard popup),
// cards render without entry/exit choreography.
interface CardAnimRefs {
  decks:    { '0': React.RefObject<HTMLDivElement | null>; '1': React.RefObject<HTMLDivElement | null> };
  discards: { '0': React.RefObject<HTMLDivElement | null>; '1': React.RefObject<HTMLDivElement | null> };
}
const CardAnimContext = createContext<CardAnimRefs | null>(null);
// True while the user is dragging — Card uses this to zero out its own
// transition prop so dnd-kit's rearrangement isn't compounded by framer's
// layout animations (which were causing other cards to "vibrate" during drag).
const DragSuppressContext = createContext<boolean>(false);
// Click-to-inspect: a Card calls this with itself to open the detail popup.
// Default no-op so Cards rendered outside the board (Collection) stay inert.
const CardInspectContext = createContext<(card: CardData) => void>(() => {});

const FLY_DURATION = 0.45;
// Hearthstone-style "easeOutBack": overshoot slightly past target then settle.
const FLY_EASE = [0.34, 1.56, 0.64, 1] as [number, number, number, number];

// --- Animation debug helpers ---
// Toggle to true (or set window.__GS_ANIM_DEBUG=true in DevTools) to flood the
// console with mount/unmount/animation lifecycle events per card. Useful when
// chasing flicker. Logs are prefixed [CARD t=Xms cardId] for quick filtering.
const DEBUG_ANIM = false;
const animT0 = (typeof performance !== 'undefined') ? performance.now() : Date.now();
function animLog(scope: string, ...args: unknown[]): void {
  const enabled = DEBUG_ANIM || (typeof window !== 'undefined' && (window as any).__GS_ANIM_DEBUG);
  if (!enabled) return;
  const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
  // eslint-disable-next-line no-console
  console.log(`[CARD t=${(now - animT0).toFixed(0)}ms ${scope}]`, ...args);
}

// --- Sub-components for DnD ---

interface CardProps {
  card: CardData;
  isHand?: boolean;
  isStaged?: boolean;
  isDragging?: boolean;
  isOverlay?: boolean;
  isGhost?: boolean;
  style?: React.CSSProperties;
  listeners?: any;
  attributes?: any;
  // Stagger offset for fly-in (e.g. dealt cards 0..3 use 0, 0.1, 0.2, 0.3).
  animDelay?: number;
  // True if this card is mounting for the first time AND wasn't visible
  // anywhere in the previous render — i.e. it's coming straight from the
  // deck. Triggers an imperative fly-in from the deck's screen rect.
  freshFromDeck?: boolean;
  // When set, overrides the card-id-prefix-derived owner for the exit-to-
  // discard animation. Required for captured cards: a card with id `p1_*`
  // can end up in player 0's stagingArea (because player 0 captured it),
  // and at stage-drain it should fly to player 0's discard pile, not the
  // original owner's. The parent passes the actual container's owner here.
  containerOwnerID?: '0' | '1';
  // Render outside game context (Collection / DeckEditor). Disables ALL
  // framer-motion behaviour — in particular `layoutId`, which otherwise
  // gets shared between the same card.id appearing in different mounted
  // trees (e.g. modal + sibling list) and triggers spurious cross-tree
  // animations. Renders as a plain div, same CSS classes.
  staticContext?: boolean;
}

// Card body shared between motion-wrapped and plain renders.
function CardContent({ card }: { card: CardData }) {
  // Dedupe effects by templateId: un template è "un'abilità" dal punto di
  // vista del giocatore. Alcuni template (es. bidirezionale) producono più
  // CardEffectInstance con lo stesso text — mostrarli ripetuti confonde.
  const displayEffects = (() => {
    if (!card.effects || card.effects.length === 0) return [];
    const seen = new Set<string>();
    const out: typeof card.effects = [];
    for (const e of card.effects) {
      if (seen.has(e.templateId)) continue;
      seen.add(e.templateId);
      out.push(e);
    }
    return out;
  })();
  const isMission = card.type === 'Mission';
  const rarity = card.rarity ?? 'street';
  const slug = SUIT_SLUG[card.suit];
  const posClass = isMission ? 'card-art-pos-mission' : `card-art-pos-${card.value}`;
  return (
    <>
      <div className={`card-art card-art-${slug}-${rarity} ${posClass}`} />
      <div className="card-val">{isMission ? 'M' : card.value}</div>
      <div className={`suit-icon suit-icon-${card.suit.replace(/\s+/g, '-')}`} title={card.suit} />
      {(() => {
        const badges = cardStateBadges(card);
        if (badges.length === 0) return null;
        return (
          <div className="card-state-badges">
            {badges.map((b, i) => (
              <div key={i} className="card-state-badge" title={b.title}>
                <span>{b.icon}</span><span>{b.value}</span>
              </div>
            ))}
          </div>
        );
      })()}
      {isMission && card.mission && (
        <div
          className="card-mission"
          style={{
            fontSize: '8px',
            color: '#fff',
            marginTop: '4px',
            lineHeight: '1.15',
            textAlign: 'center',
          }}
        >
          <div style={{ fontWeight: 'bold' }}>{card.mission.name}</div>
          <div style={{ marginTop: '3px' }}>
            <PointsBadge value={card.mission.winScore} />
          </div>
        </div>
      )}
      {displayEffects.length > 0 && (
        <div
          className="card-effects"
          style={{
            fontSize: '7px',
            fontStyle: 'italic',
            color: '#fff',
            marginTop: '2px',
            lineHeight: '1.1',
          }}
        >
          {displayEffects.map(e => (
            <div key={e.id}>{e.text}</div>
          ))}
        </div>
      )}
    </>
  );
}

// The DragOverlay's wrapper applies its own rotating transform for the
// "pendolamento" effect. A motion.div there would observe its rect changing
// every frame and trigger layout transitions that fight the CSS rotation
// (causing the scattoso feel). Render a plain div instead. Same path is
// used for staticContext (Collection / DeckEditor) to avoid layoutId
// bleeding between trees. Due componenti separati: prima era un early
// return in mezzo agli hook di AnimatedCard (violazione rules-of-hooks).
export function Card(props: CardProps) {
  if (props.isOverlay || props.staticContext) return <StaticCard {...props} />;
  return <AnimatedCard {...props} />;
}

function StaticCard({ card, isOverlay, style, listeners, attributes }: CardProps) {
  const isP1 = card.id.startsWith('p1');
  const isP0 = card.id.startsWith('p0');
  const playerClass = isP1 ? 'p1-card' : (isP0 ? 'p0-card' : '');
  const rarityClass = `rarity-${card.rarity ?? 'street'}`;
  return (
    <div
      className={`card pixelated ${isOverlay ? 'card-overlay' : ''} ${playerClass} ${rarityClass}`}
      style={style}
      {...attributes}
      {...listeners}
    >
      <CardContent card={card} />
    </div>
  );
}

function AnimatedCard({ card, isStaged, isDragging, isGhost, style, listeners, attributes, animDelay = 0, freshFromDeck, containerOwnerID }: CardProps) {
  const cardAnim = useContext(CardAnimContext);
  // Click-to-inspect plumbing. The ref records the pointer-down position so the
  // onClick can tell a genuine click (no movement) from the tail of a drag.
  const inspectCard = useContext(CardInspectContext);
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);
  const isHidden = card.id === 'hidden';
  const isP1 = card.id.startsWith('p1');
  const isP0 = card.id.startsWith('p0');
  // Original card-prefix owner — used for the fly-in source (deck the card
  // was originally drawn from).
  const owner: '0' | '1' = isP1 ? '1' : '0';
  // Current-container owner — used for the exit-to-discard destination. Falls
  // back to the prefix owner when not specified (e.g. cards in dropzones).
  const discardOwner: '0' | '1' = containerOwnerID ?? owner;
  const playerClass = isP1 ? 'p1-card' : (isP0 ? 'p0-card' : '');
  const rarityClass = `rarity-${card.rarity ?? 'street'}`;

  // Ghost / hidden cards still get a motion.div for AnimatePresence to track,
  // but no layoutId (avoid duplicate-id) and no fly-in.
  const skipAnim = isHidden || isGhost;

  const elementRef = useRef<HTMLDivElement>(null);
  // Locally tracked "is currently doing a deck → here fly-in". Starts true on
  // mount for fresh cards and flips to false when the CSS transition ends.
  // We pin to local state rather than the `freshFromDeck` prop so that if the
  // parent re-renders mid-flight (which clears the prop), framer doesn't
  // wake up and rewrite the element's transform, causing a visual jump.
  const [flying, setFlying] = useState(!!freshFromDeck);

  // --- DEBUG: mount/unmount + prop snapshot ---
  useEffect(() => {
    animLog(card.id, 'MOUNT', {
      isStaged: !!isStaged,
      isGhost: !!isGhost,
      isOverlay: false,
      isHidden,
      freshFromDeck: !!freshFromDeck,
      containerOwnerID,
      suit: card.suit,
      value: card.value,
      type: card.type,
    });
    return () => animLog(card.id, 'UNMOUNT');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Imperative fly-in from the owner's deck. Runs once on mount, ONLY for
  // cards explicitly flagged as fresh-from-deck by the parent (Board.tsx
  // tracks which cards just appeared from nowhere). We use inline style +
  // CSS transition (not framer motion values) so it doesn't fight with the
  // motion.div's animate/layoutId system. After the transition completes the
  // inline overrides are cleared and `flying` flips to false, handing control
  // back to framer for hover/drag/exit/layoutId.
  useLayoutEffect(() => {
    if (!freshFromDeck || !cardAnim || !elementRef.current) {
      animLog(card.id, 'FLY-IN skip', {
        freshFromDeck: !!freshFromDeck,
        hasCtx: !!cardAnim,
        hasEl: !!elementRef.current,
      });
      return;
    }
    const deckEl = cardAnim.decks[owner]?.current;
    if (!deckEl) {
      animLog(card.id, 'FLY-IN skip: no deck ref for owner', owner);
      return;
    }
    const el = elementRef.current;
    const cardRect = el.getBoundingClientRect();
    const deckRect = deckEl.getBoundingClientRect();
    if (cardRect.width === 0 || deckRect.width === 0) {
      animLog(card.id, 'FLY-IN abort: zero-size rect', { cardRect, deckRect });
      setFlying(false);
      return;
    }

    const dx = (deckRect.left + deckRect.width / 2) - (cardRect.left + cardRect.width / 2);
    const dy = (deckRect.top + deckRect.height / 2) - (cardRect.top + cardRect.height / 2);

    animLog(card.id, 'FLY-IN start', {
      owner,
      delay: animDelay,
      dx: dx.toFixed(0),
      dy: dy.toFixed(0),
      cardRect: { x: cardRect.left.toFixed(0), y: cardRect.top.toFixed(0) },
      deckRect: { x: deckRect.left.toFixed(0), y: deckRect.top.toFixed(0) },
    });

    // Snap to deck (no transition), force reflow, then transition to rest.
    el.style.transition = 'none';
    el.style.transform = `translate(${dx}px, ${dy}px) rotate(-180deg) scale(0.4)`;
    el.style.opacity = '0';
    void el.offsetHeight;
    el.style.transition = `transform ${FLY_DURATION}s cubic-bezier(0.34, 1.56, 0.64, 1) ${animDelay}s, opacity 0.22s ease-out ${animDelay}s`;
    el.style.transform = '';
    el.style.opacity = '';

    // Clear inline overrides + hand back to framer when the transition ends.
    let cleared = false;
    const finish = (cause: string) => {
      if (cleared) return;
      cleared = true;
      el.style.transition = '';
      el.style.transform = '';
      el.style.opacity = '';
      animLog(card.id, 'FLY-IN end', { cause });
      setFlying(false);
    };
    const onEnd = (ev: TransitionEvent) => {
      if (ev.target !== el || ev.propertyName !== 'transform') return;
      finish('transitionend');
      el.removeEventListener('transitionend', onEnd);
    };
    el.addEventListener('transitionend', onEnd);
    // Safety: if transitionend doesn't fire (e.g. animation interrupted),
    // force-finish a bit after the expected end time.
    const totalMs = (FLY_DURATION + animDelay) * 1000 + 100;
    const safety = setTimeout(() => finish('safety-timeout'), totalMs);
    return () => {
      el.removeEventListener('transitionend', onEnd);
      clearTimeout(safety);
      animLog(card.id, 'FLY-IN cleanup');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Custom exit: fly to the discard pile of whoever currently holds the card
  // (the captor, set via containerOwnerID), not the original card-prefix
  // owner. Both captured-yours and captured-opp's cards in the same staging
  // area should fly to the same discard.
  const exitToDiscard = () => {
    if (!cardAnim || !elementRef.current) {
      return { opacity: 0, scale: 0.5, transition: { duration: 0.25 } };
    }
    const discardEl = cardAnim.discards[discardOwner]?.current;
    if (!discardEl) return { opacity: 0, scale: 0.5, transition: { duration: 0.25 } };
    const cardRect = elementRef.current.getBoundingClientRect();
    const discardRect = discardEl.getBoundingClientRect();
    const dx = (discardRect.left + discardRect.width / 2) - (cardRect.left + cardRect.width / 2);
    const dy = (discardRect.top + discardRect.height / 2) - (cardRect.top + cardRect.height / 2);
    return {
      x: dx,
      y: dy,
      rotate: 360,
      scale: 0.3,
      opacity: 0,
      transition: { duration: 0.4, ease: 'easeIn' as const },
    };
  };

  // Rest state for cards not currently doing a fly-in. EVERY framer-controlled
  // property has an explicit value so framer always knows what to restore after
  // a whileHover ends or an initial fires (otherwise scale/rotateY get stuck at
  // their initial values and cards look small or mirrored).
  const animateTarget = flying
    ? undefined  // CSS transition from useLayoutEffect handles the entry
    : {
        opacity: isDragging ? 0 : (isGhost ? 0.4 : 1),
        y: isStaged ? -5 : 0,
        scale: 1,
        rotate: 0,
        rotateY: 0,
      };

  const dragSuppressed = useContext(DragSuppressContext);
  // During drag, zero out the transition so layout animations (from cards
  // rearranging around the ghost-preview slot) don't show as 200ms tweens —
  // which manifested as the "altre carte vibrano" feedback. dnd-kit's own
  // sortable transition still handles the visible rearrangement.
  const motionTransition = dragSuppressed
    ? { duration: 0 }
    : { duration: 0.2, ease: 'easeOut' as const };

  return (
    <motion.div
      ref={elementRef}
      // layoutId stays set even during drag so when the dragged card transitions
      // from hand-DraggableCard → dropzone-SortableCard (post-drop), framer's
      // FLIP rescues the visual continuity. Skipped only for ghost/hidden/overlay.
      // What we DO suppress during drag is layout="position" (which would
      // otherwise animate the sibling cards' grid-cell shifts as the ghost
      // moves around), and initial (which would fade-in any mid-drag mounts).
      layoutId={skipAnim ? undefined : `card-${card.id}`}
      layout={dragSuppressed ? false : "position"}
      initial={skipAnim || flying || dragSuppressed ? false : { opacity: 0, scale: 0.85 }}
      animate={animateTarget}
      exit={skipAnim ? { opacity: 0, transition: { duration: 0.15 } } : exitToDiscard}
      whileHover={skipAnim || isStaged || isDragging || flying || dragSuppressed ? undefined : { y: -10, scale: 1.15, transition: { duration: 0.15 } }}
      transition={motionTransition}
      style={style}
      className={`card pixelated ${isStaged ? 'staged' : ''} ${playerClass} ${rarityClass} ${isDragging ? 'dragging' : ''} ${isGhost ? 'ghost' : ''} ${(card.frozenTurns ?? 0) > 0 ? 'frozen' : ''}`}
      // Capture-phase pointer-down records the start position WITHOUT touching
      // dnd-kit's own onPointerDown (bubble phase). onClick then fires the
      // inspect popup only if the pointer barely moved — a drag moves >8px.
      onPointerDownCapture={(e) => { pointerDownPos.current = { x: e.clientX, y: e.clientY }; }}
      onClick={(e) => {
        if (isHidden || isGhost) return;
        const d = pointerDownPos.current;
        if (d && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 8) inspectCard(card);
      }}
      onAnimationStart={(def) => animLog(card.id, 'ANIM start', def)}
      onAnimationComplete={(def) => animLog(card.id, 'ANIM complete', def)}
      onLayoutAnimationStart={() => animLog(card.id, 'LAYOUT start')}
      onLayoutAnimationComplete={() => animLog(card.id, 'LAYOUT complete')}
      {...attributes}
      {...listeners}
    >
      <CardContent card={card} />
    </motion.div>
  );
}

function DraggableCard({
  card, isHand, disabled, index, total, animDelay, freshFromDeck,
}: {
  card: CardData; isHand: boolean; disabled: boolean; index: number; total: number; animDelay?: number; freshFromDeck?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: card.id,
    disabled: !isHand || disabled,
    data: { card }
  });

  // Fan-shape geometry: each card rotates around its bottom-center, with a
  // subtle parabolic dip at the edges so the hand reads as an arc.
  // Center card = 0 offset; edges grow ±. Tuned for hand sizes 1..4.
  const center = total > 1 ? (total - 1) / 2 : 0;
  const offset = index - center;
  const ROT_PER_CARD = 6;       // degrees
  const Y_FACTOR = 7;           // px per offset² (downward dip)
  const rotateDeg = offset * ROT_PER_CARD;
  const translateY = Math.pow(offset, 2) * Y_FACTOR;

  // While dragging, follow the pointer cleanly without the fan rotation
  // (composing the fan + drag transforms feels jittery).
  const style = isDragging
    ? { transform: CSS.Translate.toString(transform) }
    : {
        transform: `translateY(${translateY}px) rotate(${rotateDeg}deg)`,
        transformOrigin: 'center bottom',
        transition: 'transform 0.2s ease',
      };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        card={card}
        isHand={isHand}
        isDragging={isDragging}
        listeners={listeners}
        attributes={attributes}
        animDelay={animDelay}
        freshFromDeck={freshFromDeck}
      />
    </div>
  );
}

function DroppableDropzone({ id, children, items }: { id: string, children: React.ReactNode, items: string[] }) {
  const { isOver, setNodeRef } = useDroppable({
    id: id,
  });

  const style = {
    backgroundColor: isOver ? 'rgba(79, 195, 247, 0.2)' : undefined,
    borderColor: isOver ? 'var(--accent-color)' : undefined,
  };

  return (
    <div id={id} ref={setNodeRef} className="dropzone" style={style}>
      <SortableContext items={items} strategy={rectSortingStrategy}>
        {children}
      </SortableContext>
    </div>
  );
}

function SortableCard({ card, isStaged, isGhost, animDelay, freshFromDeck }: { card: CardData, isStaged?: boolean, isGhost?: boolean, animDelay?: number, freshFromDeck?: boolean }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: isGhost ? card.id + '-ghost' : card.id,
    disabled: false
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        card={card}
        isStaged={isStaged}
        isDragging={isDragging}
        isGhost={isGhost}
        attributes={isGhost ? attributes : undefined}
        listeners={isGhost ? listeners : undefined}
        animDelay={animDelay}
        freshFromDeck={freshFromDeck}
      />
    </div>
  );
}

function DiscardPopup({ cards, onClose }: { cards: CardData[]; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Sort: hand cards first by suit then value, then mission cards (the trashed
  // mission card lives in discard too).
  const handCards = cards.filter(c => c.type === 'Hand');
  const missionCards = cards.filter(c => c.type === 'Mission');
  handCards.sort((a, b) =>
    a.suit === b.suit ? a.value - b.value : a.suit.localeCompare(b.suit)
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1a1a24', border: '2px solid var(--accent-color)',
          borderRadius: '8px', padding: '20px', minWidth: '600px',
          maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto',
          color: 'var(--text-color)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ margin: 0, color: 'var(--accent-color)' }}>
            Your Captures ({handCards.length} cards{missionCards.length > 0 ? ` + ${missionCards.length} discarded mission` : ''})
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: '1px solid #555', color: '#ccc', padding: '4px 12px', cursor: 'pointer', borderRadius: '4px' }}
          >
            Close (Esc)
          </button>
        </div>

        {handCards.length === 0 && missionCards.length === 0 ? (
          <p style={{ color: '#888' }}>You haven't captured any cards yet.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {handCards.map(c => <Card key={c.id} card={c} />)}
            {missionCards.map(c => <Card key={c.id} card={c} />)}
          </div>
        )}
      </div>
    </div>
  );
}

// Detail popup opened by clicking a card. Shows the card large, a verbose
// explanation of each effect, and the live "Current status" (counters + who
// froze it, if anyone).
function CardDetailPopup({ card, onClose }: { card: CardData; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isMission = card.type === 'Mission';
  const title = isMission
    ? (card.mission?.name ?? 'Mission card')
    : `${card.value} of ${card.suit}`;

  // Dedupe effects by templateId (same rule as the card face).
  const seen = new Set<string>();
  const effects = (card.effects ?? []).filter(e => {
    if (seen.has(e.templateId)) return false;
    seen.add(e.templateId);
    return true;
  });
  const badges = cardStateBadges(card);
  const frozen = (card.frozenTurns ?? 0) > 0;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1a1a24', border: '2px solid var(--accent-color)',
          borderRadius: 8, padding: 20, width: 520, maxWidth: '92vw',
          maxHeight: '88vh', overflowY: 'auto', color: 'var(--text-color)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ margin: 0, color: 'var(--accent-color)', fontSize: 18, textTransform: 'capitalize' }}>{title}</h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: '1px solid #555', color: '#ccc', padding: '4px 12px', cursor: 'pointer', borderRadius: 4 }}
          >
            Close (Esc)
          </button>
        </div>

        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          {/* Card shown at 2× via `zoom` to appreciate the pixel art. zoom —
              unlike transform:scale — also reshapes layout, so the bigger card
              genuinely takes 2× space: it never overlaps the text column nor
              the status box, and the popup sizes itself correctly. */}
          <div style={{ flexShrink: 0, zoom: 2 }}>
            <Card card={card} staticContext />
          </div>
          <div style={{ flex: 1, fontSize: 12, lineHeight: 1.5, minWidth: 0 }}>
            {isMission && card.mission && (
              <>
                <div style={{ color: '#bbb', fontStyle: 'italic', marginBottom: 8 }}>{card.mission.fluff}</div>
                <PointsBadge value={card.mission.winScore} />
              </>
            )}
            {!isMission && effects.length === 0 && (
              <div style={{ color: '#888' }}>This card has no special effect.</div>
            )}
            {effects.map(e => (
              <div key={e.id} style={{ marginBottom: 12 }}>
                <div style={{ color: '#ffd24a', fontWeight: 'bold', marginBottom: 2 }}>{e.text}</div>
                <div style={{ color: '#ccc' }}>{describeEffect(e)}</div>
              </div>
            ))}
          </div>
        </div>

        {(frozen || badges.length > 0) && (
          <div style={{ marginTop: 14, padding: '10px 12px', background: '#0f0f18', border: '1px solid #333', borderRadius: 6 }}>
            <div style={{ color: 'var(--accent-color)', fontWeight: 'bold', fontSize: 12, marginBottom: 6 }}>Current status</div>
            {frozen && (
              <div style={{ fontSize: 12, color: '#9fd3ff', marginBottom: 4 }}>
                ❄ Frozen — cannot be captured for {card.frozenTurns} more round{card.frozenTurns === 1 ? '' : 's'}
                {card.frozenBy ? <> · locked down by the <strong>{card.frozenBy}</strong></> : null}
              </div>
            )}
            {badges.filter(b => b.icon !== '❄').map((b, i) => (
              <div key={i} style={{ fontSize: 12, color: '#ddd', marginBottom: 4 }}>
                {b.icon} {b.title}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface UserDeckRecord {
  id: string;
  name: string;
  createdAt: string;
  cards: { id: string; card: CardData; acquiredAt: string; source: string }[];
}

function readAuthToken(): string | null {
  try {
    const raw = localStorage.getItem('gs_auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: string };
    return parsed?.token ?? null;
  } catch { return null; }
}

function readAuthAvatarId(): number | null {
  try {
    const raw = localStorage.getItem('gs_auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { user?: { avatarId?: number | null } };
    const v = parsed?.user?.avatarId;
    return v === null || v === undefined ? null : Number(v);
  } catch { return null; }
}

function DraftView({
  isReady, isBot, localDraftSuits, setLocalDraftSuits, onPickSuits, onPickDeck,
}: {
  isReady: boolean;
  isBot: boolean;
  localDraftSuits: string[];
  setLocalDraftSuits: (next: string[]) => void;
  onPickSuits: (suits: string[]) => void;
  // F7 v2 friendly: il picker passa solo `deckId` (string). Il parent
  // chiama /auth/matches/:matchID/commit-deck server-side e poi fa
  // moves.selectDeck({ avatarId }) per confermare. Le carte non viaggiano
  // più come move payload.
  onPickDeck: (deckId: string) => Promise<void>;
}) {
  const [decks, setDecks] = useState<UserDeckRecord[] | null>(null);
  const [loadingDecks, setLoadingDecks] = useState(false);
  const [decksError, setDecksError] = useState<string | null>(null);
  const [picking, setPicking] = useState<string | null>(null); // deckId in flight
  const token = isBot ? null : readAuthToken();

  useEffect(() => {
    if (!token || isReady) return;
    let cancelled = false;
    setLoadingDecks(true);
    (async () => {
      try {
        const res = await fetch(`${SERVER_ORIGIN}/auth/decks`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setDecksError(data.error ?? 'failed to load decks');
        } else {
          setDecks(data.decks as UserDeckRecord[]);
        }
      } catch (e) {
        if (!cancelled) setDecksError(e instanceof Error ? e.message : 'network error');
      } finally {
        if (!cancelled) setLoadingDecks(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, isReady]);

  const toggleSuit = (suit: string) => {
    if (localDraftSuits.includes(suit)) {
      setLocalDraftSuits(localDraftSuits.filter(s => s !== suit));
    } else if (localDraftSuits.length < 2) {
      setLocalDraftSuits([...localDraftSuits, suit]);
    }
  };

  // Already confirmed → wait for opponent
  if (isReady) {
    return (
      <div className="board gameover-screen ui-scaled" style={{ justifyContent: 'center' }}>
        <h1>DRAFT PHASE</h1>
        <h2>Waiting for opponent...</h2>
        <div style={{ marginTop: '20px' }}>
          <button
            className="multi-btn"
            style={{ backgroundColor: '#555' }}
            onClick={() => window.dispatchEvent(new Event('app-reset'))}
          >
            Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  // Bot mode: the auto-driver picks random suits after a short delay.
  // Show a banner — no manual interaction needed.
  if (isBot) {
    return (
      <div className="board gameover-screen ui-scaled" style={{ justifyContent: 'center' }}>
        <h1>DRAFT PHASE</h1>
        <h2 style={{ color: '#ff3366' }}>🤖 BOT MODE — drafting random suits...</h2>
      </div>
    );
  }

  // Logged-in user with a token: show their decks.
  if (token) {
    if (loadingDecks) {
      return (
        <div className="board gameover-screen ui-scaled" style={{ justifyContent: 'center' }}>
          <h1>DRAFT PHASE</h1>
          <h2 style={{ color: '#888' }}>Loading decks...</h2>
          <div style={{ marginTop: '20px' }}>
            <button
              className="multi-btn"
              style={{ backgroundColor: '#555' }}
              onClick={() => window.dispatchEvent(new Event('app-reset'))}
            >
              Return to Lobby
            </button>
          </div>
        </div>
      );
    }
    if (decksError) {
      return (
        <div className="board gameover-screen ui-scaled" style={{ justifyContent: 'center' }}>
          <h1>DRAFT PHASE</h1>
          <h2 style={{ color: '#e57373' }}>Error: {decksError}</h2>
          <div style={{ marginTop: '20px' }}>
            <button
              className="multi-btn"
              style={{ backgroundColor: '#555' }}
              onClick={() => window.dispatchEvent(new Event('app-reset'))}
            >
              Return to Lobby
            </button>
          </div>
        </div>
      );
    }
    if (decks && decks.length > 0) {
      return (
        <div className="board gameover-screen ui-scaled" style={{ justifyContent: 'center' }}>
          <h1>DRAFT PHASE</h1>
          <h2 style={{ marginBottom: '20px' }}>Pick a Deck</h2>
          <div style={{ color: '#888', fontSize: 11, marginBottom: 10, textAlign: 'center', maxWidth: 720 }}>
            Only complete decks (14 cards, 2 suits, 6 Hand + 1 Mission per suit) can be played.
            Greyed-out decks are missing cards — fix them in Your Cards first.
          </div>
          {decksError && (
            <div style={{ color: '#e57373', fontSize: 12, marginBottom: 10 }}>{decksError}</div>
          )}
          <div id="tutorial-suit-picker" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '900px', maxHeight: '70vh', overflowY: 'auto', padding: '4px 12px' }}>
            {decks.map(deck => {
              const cards = deck.cards.map(r => r.card);
              const suitsInDeck = Array.from(new Set(cards.map(c => c.suit))) as Suit[];
              const handCount = cards.filter(c => c.type === 'Hand').length;
              const missionCount = cards.filter(c => c.type === 'Mission').length;
              // Pure-data validator — same rules the server enforces at
              // commit-deck. Decks that don't pass become unclickable here
              // so the bug "picker shows 12-card deck → match wedges at
              // end" can't happen.
              const validation = validateDeckComposition(cards);
              const isValid = validation.valid;
              const reason = !isValid ? validation.reason : '';
              const inFlight = picking === deck.id;
              const handlePick = async () => {
                if (!isValid || picking) return;
                setPicking(deck.id);
                setDecksError(null);
                try {
                  await onPickDeck(deck.id);
                } catch (e) {
                  setDecksError(e instanceof Error ? e.message : 'commit failed');
                } finally {
                  setPicking(null);
                }
              };
              return (
                <button
                  key={deck.id}
                  onClick={handlePick}
                  disabled={!isValid || !!picking}
                  title={isValid ? '' : `Cannot play: ${reason}`}
                  style={{
                    background: isValid ? '#1a1a24' : '#15151a',
                    border: `2px solid ${isValid ? 'var(--accent-color)' : '#444'}`,
                    borderRadius: '8px', padding: '20px 24px', minWidth: '240px',
                    cursor: isValid && !picking ? 'pointer' : 'not-allowed',
                    fontFamily: 'inherit', color: 'var(--text-color)',
                    textAlign: 'left', opacity: isValid ? 1 : 0.45,
                    position: 'relative',
                  }}
                >
                  <div style={{ color: isValid ? '#ffb703' : '#888', fontWeight: 'bold', fontSize: '15px', marginBottom: '6px' }}>
                    {inFlight ? 'Committing…' : deck.name}
                  </div>
                  <div style={{ color: '#aaa', fontSize: '11px', marginBottom: '8px' }}>
                    {cards.length} cards ({handCount} hand + {missionCount} missions)
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {suitsInDeck.map(s => (
                      <span key={s} style={{
                        background: '#333', color: '#ddd', fontSize: '10px',
                        padding: '2px 6px', borderRadius: '3px',
                      }}>{s}</span>
                    ))}
                  </div>
                  {!isValid && (
                    <div style={{
                      marginTop: 8, color: '#e57373', fontSize: 10, lineHeight: 1.3,
                    }}>⚠ {reason}</div>
                  )}
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: '30px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              className="multi-btn"
              style={{ backgroundColor: '#555' }}
              onClick={() => window.dispatchEvent(new Event('app-reset'))}
            >
              Return to Lobby
            </button>
            <button
              className="multi-btn"
              style={{ backgroundColor: '#3f51b5' }}
              onClick={() => window.dispatchEvent(new Event('app-reset-to-collection'))}
            >
              Go to Deck Builder
            </button>
          </div>
        </div>
      );
    }
  }

  // Fallback: no token (shouldn't happen in auth-gated app) or no decks.
  // Keep the legacy suit picker so the user can still play.
  return (
    <div className="board gameover-screen ui-scaled" style={{ justifyContent: 'center' }}>
      <h1>DRAFT PHASE</h1>
      <div id="tutorial-suit-picker" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <h2 style={{ marginBottom: '20px' }}>Pick 2 Suits for Your Deck</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '30px' }}>
          {SUITS.map(suit => (
            <button
              key={suit}
              style={{ minWidth: '120px' }}
              className={`multi-btn ${localDraftSuits.includes(suit) ? '' : 'btn-secondary'}`}
              onClick={() => toggleSuit(suit)}
            >
              {suit}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="multi-btn"
            style={{ backgroundColor: '#555' }}
            onClick={() => window.dispatchEvent(new Event('app-reset'))}
          >
            Return to Lobby
          </button>
          {token && (
            <button
              className="multi-btn"
              style={{ backgroundColor: '#3f51b5' }}
              onClick={() => window.dispatchEvent(new Event('app-reset-to-collection'))}
            >
              Go to Deck Builder
            </button>
          )}
          <button
            className="multi-btn"
            disabled={localDraftSuits.length !== 2}
            onClick={() => onPickSuits(localDraftSuits)}
          >
            Confirm Deck
          </button>
        </div>
      </div>
    </div>
  );
}

// Wrapper: il caso spettatore (playerID null, G = {spectatorDenied}) esce qui,
// PRIMA di qualsiasi hook. Prima l'early return stava a metà componente,
// dopo alcuni hook e prima di altri (violazione rules-of-hooks).
export function GrandSnatchBoard(props: GrandSnatchBoardProps) {
  if (!props.playerID || !props.G?.players) {
    return <div className="board error">You are acting as a spectator! Please set playerID.</div>;
  }
  return <GrandSnatchBoardInner {...props} />;
}

function GrandSnatchBoardInner({ G, ctx, moves, playerID: playerIDProp, matchData, matchID }: GrandSnatchBoardProps) {
  // Garantito non-null dal wrapper GrandSnatchBoard.
  const playerID = playerIDProp as string;
  const isBot = useContext(BotContext);
  const runMatchCtx = useContext(RunMatchContext);
  const { active: isTutorial } = useContext(TutorialContext);
  const [tutorialDismissed, setTutorialDismissed] = useState(false);
  const showTutorial = isTutorial && !tutorialDismissed;

  const lastSnatchCountRef = useRef(G.matchSummary?.snatchEvents?.length ?? 0);
  const [snatchNotification, setSnatchNotification] = useState<{ text: string; isMe: boolean; isBoth: boolean } | null>(null);

  // Telemetry: track match start
  useEffect(() => {
    trackEvent('match_started', {
      matchId: matchID,
      mode: isTutorial ? 'tutorial' : (runMatchCtx.deckUsedKey ? 'run' : 'pvp'),
      deckUsedKey: runMatchCtx.deckUsedKey,
      playerID,
    });
  }, []);

  useEffect(() => {
    if (!playerID) return;
    const oppID = playerID === '0' ? '1' : '0';
    const currentEvents = G.matchSummary?.snatchEvents ?? [];
    const lastCount = lastSnatchCountRef.current;

    if (currentEvents.length > lastCount) {
      const newEvents = currentEvents.slice(lastCount);
      const containsMe = newEvents.some(e => e.snatcherID === playerID);
      const containsOpp = newEvents.some(e => e.snatcherID === oppID);

      let text = '';
      let isMe = false;
      let isBoth = false;

      if (containsMe && containsOpp) {
        text = 'DOUBLE SNATCH!';
        isBoth = true;
      } else if (containsMe) {
        text = 'YOU SNATCHED!';
        isMe = true;
      } else if (containsOpp) {
        const oppName = matchData?.find(m => String(m.id) === oppID)?.name;
        text = oppName === '[BOT]' ? 'BOT SNATCHED!' : 'OPPONENT SNATCHED!';
      }

      setSnatchNotification({ text, isMe, isBoth });
      lastSnatchCountRef.current = currentEvents.length;

      const t = setTimeout(() => setSnatchNotification(null), 2500);
      return () => clearTimeout(t);
    } else {
      lastSnatchCountRef.current = currentEvents.length;
    }
  }, [G.matchSummary?.snatchEvents?.length, playerID]);

  // Auto-fire selectDeck quando il match parte da dentro a una run.
  // Post-F7 v2: il server pre-popola `G.players[pid].selectedCards` da
  // setupData.runMatch.deckCards (vedi Game.ts:getInitialState). Il client
  // non manda più cards né suits — solo l'avatarId per la cosmetica.
  // Triggher: `selectedCards` non vuoto + `readyToPlay` ancora false.
  useEffect(() => {
    if (!playerID) return;
    if (ctx.phase !== 'draft') return;
    const me = G.players[playerID];
    if (!me || me.readyToPlay) return;
    if (!me.selectedCards || me.selectedCards.length === 0) return;
    let avatarId: number | null = null;
    try {
      const raw = localStorage.getItem('gs_auth');
      if (raw) avatarId = (JSON.parse(raw) as any)?.user?.avatarId ?? null;
    } catch { /* ignore */ }
    moves.selectDeck({ avatarId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.phase, playerID, G.players[playerID!]?.readyToPlay, G.players[playerID!]?.selectedCards?.length]);

  // Hook gameover: fire onMatchEnd una sola volta quando ctx.gameover diventa
  // truthy, così App.tsx POSTa il record-match al server. Passa ctx.gameover
  // (winner reale + score) così l'advance-to-node può usarlo invece di
  // approssimare con i soli snatchPoints.
  const matchEndFiredRef = useRef(false);
  useEffect(() => {
    if (!ctx.gameover) { matchEndFiredRef.current = false; return; }
    if (matchEndFiredRef.current) return;
    matchEndFiredRef.current = true;

    // Telemetry: track match end
    const rounds = G.roundCount;
    const isMeWinner = ctx.gameover.winner === playerID;
    // Il pareggio arriva come { draw: true } senza winner (vedi Game.ts endIf).
    const isDraw = Boolean(ctx.gameover.draw) || !ctx.gameover.winner;
    const outcome = isDraw ? 'draw' : (isMeWinner ? 'won' : 'lost');
    const myScore = playerID === '0' ? (ctx.gameover.score0 ?? 0) : (ctx.gameover.score1 ?? 0);
    const oppScore = playerID === '0' ? (ctx.gameover.score1 ?? 0) : (ctx.gameover.score0 ?? 0);
    const mySnatchesCount = G.matchSummary?.snatchEvents?.filter(e => e.snatcherID === playerID).length ?? 0;

    trackEvent('match_ended', {
      matchId: matchID,
      mode: isTutorial ? 'tutorial' : (runMatchCtx.deckUsedKey ? 'run' : 'pvp'),
      outcome,
      rounds,
      scores: playerID ? { me: myScore, opponent: oppScore } : null,
      snatchesCount: mySnatchesCount,
    });

    trackEvent('match_debrief_viewed', {
      matchId: matchID,
      mode: isTutorial ? 'tutorial' : (runMatchCtx.deckUsedKey ? 'run' : 'pvp'),
      outcome,
      rounds,
      scores: playerID ? { me: myScore, opponent: oppScore } : null,
    });

    if (!runMatchCtx.onMatchEnd) return;
    if (!playerID) return;
    if (!matchID) return;
    // matchID is forwarded from bgio's own prop — see RunMatchCtx for why we
    // don't rely on App's matchID state (closure stale at bind time).
    runMatchCtx.onMatchEnd(G, playerID, ctx.gameover, matchID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.gameover]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [rotation, setRotation] = useState(0);
  const lastDeltaX = useRef(0);
  const [viewingDiscard, setViewingDiscard] = useState(false);
  // Card clicked for the detail popup (null = popup closed).
  const [inspectedCard, setInspectedCard] = useState<CardData | null>(null);
  const rotationTimer = useRef<any>(null);

  // Refs to the deck and discard pile placeholders, per player. Card components
  // read these via CardAnimContext to compute fly-in source (deck) and fly-out
  // destination (discard) coordinates. Refs are stable for the life of the
  // component; the .current is read at animation time so DOM resizing is OK.
  const myDeckRef    = useRef<HTMLDivElement | null>(null);
  const oppDeckRef   = useRef<HTMLDivElement | null>(null);
  const myDiscardRef = useRef<HTMLDivElement | null>(null);
  const oppDiscardRef= useRef<HTMLDivElement | null>(null);
  const cardAnimCtx: CardAnimRefs = playerID === '1'
    ? { decks: { '0': oppDeckRef, '1': myDeckRef }, discards: { '0': oppDiscardRef, '1': myDiscardRef } }
    : { decks: { '0': myDeckRef, '1': oppDeckRef }, discards: { '0': myDiscardRef, '1': oppDiscardRef } };


  const myHand = G.players[playerID].hand;
  const myResources = G.players[playerID].resources;
  const myPoints = G.players[playerID].snatchPoints;
  const pState = G.players[playerID];

  // Fresh-from-deck detection: any card id that's now visible but wasn't in
  // the previous render came from a deck (the deck is the only "source"
  // outside the visible tree — discards exist as visible-only-via-popup). We
  // pass freshFromDeck={true} for these so Card runs its imperative fly-in
  // from the owner's deck rect.
  const prevVisibleIdsRef = useRef<Set<string>>(new Set());
  const visibleIds = useMemo(() => {
    const s = new Set<string>();
    for (const p of ['0', '1'] as const) {
      const pl = G.players[p];
      pl.hand.forEach(c => { if (c.id !== 'hidden') s.add(c.id); });
      pl.stagingArea.forEach(c => s.add(c.id));
      const sm = G.stagedMoves[p];
      if (sm && sm.card.id !== 'hidden') s.add(sm.card.id);
    }
    G.dropzones.forEach(dz => dz.cards.forEach(c => s.add(c.id)));
    return s;
  }, [G]);
  const freshIds = useMemo(() => {
    const fresh = new Set<string>();
    for (const id of visibleIds) {
      if (!prevVisibleIdsRef.current.has(id)) fresh.add(id);
    }
    return fresh;
  }, [visibleIds]);
  // DEBUG: render-level snapshot. Logs phase, fresh ids, staged moves so we
  // can correlate the per-card lifecycle with what the parent thinks.
  animLog('BOARD', 'render', {
    phase: ctx.phase,
    round: G.roundCount,
    activeId,
    fresh: [...freshIds],
    visibleCount: visibleIds.size,
    stagedMoves: {
      '0': G.stagedMoves['0'] ? { id: G.stagedMoves['0']!.card.id, dz: G.stagedMoves['0']!.dropzoneIndex, t: G.stagedMoves['0']!.targetIndex } : null,
      '1': G.stagedMoves['1'] ? { id: G.stagedMoves['1']!.card.id, dz: G.stagedMoves['1']!.dropzoneIndex, t: G.stagedMoves['1']!.targetIndex } : null,
    },
    confirmed: {
      '0': { play: G.players['0'].playConfirmed, stage: G.players['0'].stageConfirmed },
      '1': { play: G.players['1'].playConfirmed, stage: G.players['1'].stageConfirmed },
    },
  });
  useEffect(() => {
    prevVisibleIdsRef.current = visibleIds;
  }, [visibleIds]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    animLog('BOARD', 'DRAG start', { id: event.active.id });
    setActiveId(event.active.id as string);
    setRotation(0);
    lastDeltaX.current = 0;
  };

  const handleDragMove = (event: any) => {
    const { delta } = event;

    // Calculate velocity (difference from last frame)
    const velocity = delta.x - lastDeltaX.current;
    lastDeltaX.current = delta.x;

    // Map velocity to rotation (e.g., 1px of movement = 0.5 degrees)
    // We cap it at 15 degrees for safety
    const newRotation = Math.max(-15, Math.min(15, velocity * 0.8));
    setRotation(newRotation);

    // Reset rotation back to 0 when movement stops
    if (rotationTimer.current) clearTimeout(rotationTimer.current);
    rotationTimer.current = setTimeout(() => {
      setRotation(0);
    }, 50);
  };

  // Local state to manage the visual "gap" when dragging from hand to dropzone
  const [clonedItems, setClonedItems] = useState<{ [key: string]: string[] }>({
    'dz-0': [],
    'dz-1': []
  });

  // Sync cloned items when G updates (only when not dragging)
  useEffect(() => {
    if (!activeId) {
      setClonedItems({
        'dz-0': G.dropzones[0].cards.map(c => c.id),
        'dz-1': G.dropzones[1].cards.map(c => c.id)
      });
    }
  }, [G.dropzones, activeId]);

  const [localDraftSuits, setLocalDraftSuits] = useState<string[]>([]);

  const activeCard = activeId
    ? myHand?.find((c: any) => c.id === activeId)
    : null;

  useEffect(() => {
    if (!isBot || !playerID || ctx.gameover) return;

    const pStateCurrent = G.players[playerID];
    if (!pStateCurrent) return;

    const needsToPlay = ctx.phase === 'play' && !pStateCurrent.playConfirmed;
    const needsToStage = ctx.phase === 'stage' && !pStateCurrent.stageConfirmed;
    const needsToDraft = ctx.phase === 'draft' && !pStateCurrent.readyToPlay;

    if (!needsToPlay && !needsToStage && !needsToDraft) return;

    const timer = setTimeout(() => {
      if (needsToDraft) {
        // Shuffle the suits and pick the first two randomly. Bot doesn't have
        // an account / collection so it falls back to procedural deck generation.
        const shuffled = [...SUITS].sort(() => 0.5 - Math.random());
        moves.selectDeck({ suits: [shuffled[0], shuffled[1]] });
      } else if (needsToPlay) {
        if (!G.stagedMoves[playerID] && pStateCurrent.hand.length > 0) {
          const randomCardIndex = Math.floor(Math.random() * pStateCurrent.hand.length);
          const randomDropzone = Math.floor(Math.random() * 2);
          const targetIdx = G.dropzones[randomDropzone].cards.length;
          moves.playCard(pStateCurrent.hand[randomCardIndex].id, randomDropzone, targetIdx);
          setTimeout(() => moves.confirmPlay(), 800);
        }
      } else if (needsToStage) {
        moves.confirmStage();
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [
    isBot,
    playerID,
    ctx.phase,
    playerID ? G.players[playerID]?.playConfirmed : undefined,
    playerID ? G.players[playerID]?.stageConfirmed : undefined,
    playerID ? G.players[playerID]?.readyToPlay : undefined,
    ctx.gameover
  ]);


  if (ctx.phase === 'draft') {
    const myAvatarId = readAuthAvatarId();
    return (
      <>
        <DraftView
          isReady={G.players[playerID].readyToPlay}
          isBot={isBot}
          localDraftSuits={localDraftSuits}
          setLocalDraftSuits={setLocalDraftSuits}
          onPickSuits={(s) => moves.selectDeck({ suits: s, avatarId: myAvatarId })}
          onPickDeck={async (deckId) => {
            // F7 v2 friendly: il client manda solo `deckId`, il server pesca
            // le carte dal DB e mutate G.players[seat].selectedCards. Dopo
            // la mutation server-side facciamo selectDeck({avatarId}) per
            // confermare la readyToPlay e triggerare il broadcast bgio (la
            // mutation diretta via setState non broadcasta da sola; il move
            // successivo invece sì, e il branch run-mode ignora cards/suits
            // dal payload perché selectedCards è già non-vuoto in G).
            const token = readAuthToken();
            if (!token || !matchID) throw new Error('not authenticated or no match');
            const res = await fetch(`${SERVER_ORIGIN}/auth/matches/${matchID}/commit-deck`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ deckId }),
            });
            if (!res.ok) {
              const j = await res.json().catch(() => ({}));
              throw new Error(j.error ?? 'commit-deck failed');
            }
            moves.selectDeck({ avatarId: myAvatarId });
          }}
        />
        {showTutorial && (
          <TutorialOverlay
            G={G}
            phase={ctx.phase}
            gameover={ctx.gameover}
            onDismiss={() => setTutorialDismissed(true)}
          />
        )}
      </>
    );
  }

  const oppID = playerID === '0' ? '1' : '0';
  const oppState = G.players[oppID];
  const oppPoints = oppState.snatchPoints;
  const oppResources = oppState.resources;

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    const activeId = active.id.toString();
    const ghostId = `${activeId}-ghost`;

    if (!over) {
      // If we are not hovering over anything, remove the ghost from preview
      setClonedItems({
        'dz-0': G.dropzones[0].cards.map(c => c.id),
        'dz-1': G.dropzones[1].cards.map(c => c.id)
      });
      return;
    }

    const overId = over.id.toString();

    // 1. Find the target container
    const overContainer = overId.startsWith('dz-')
      ? overId
      : Object.keys(clonedItems).find(key => clonedItems[key].includes(overId));

    if (!overContainer) {
      // If we are hovering over something that is NOT a dropzone, remove the ghost
      setClonedItems({
        'dz-0': G.dropzones[0].cards.map(c => c.id),
        'dz-1': G.dropzones[1].cards.map(c => c.id)
      });
      return;
    }

    // We use a specific ID format for the ghost to avoid conflicts with the original card in hand
    //const ghostId = `${activeId}-ghost`;

    // 2. Determine indices
    const overItems = clonedItems[overContainer];
    const overIndex = overItems.indexOf(overId);
    const activeIndex = overItems.indexOf(ghostId);
    const activeContainer = Object.keys(clonedItems).find(key => clonedItems[key].includes(ghostId));

    // 3. Optimization: Calculate where to insert
    let projectedIndex: number;

    if (overIndex >= 0) {
      // Over a specific card, use its index
      projectedIndex = overIndex;
    } else {
      // Over the container itself (empty space)
      const containerElement = document.getElementById(overContainer);
      const activeRect = active.rect.current.translated;
      if (containerElement && activeRect) {
        const rect = containerElement.getBoundingClientRect();
        const midX = rect.left + rect.width / 2;
        const activeMidX = activeRect.left + activeRect.width / 2;
        projectedIndex = activeMidX < midX ? 0 : overItems.length;
      } else {
        projectedIndex = overItems.length;
      }
    }

    if (activeContainer === overContainer && activeIndex === projectedIndex) return;

    setClonedItems(prev => {
      const currentActiveContainer = Object.keys(prev).find(key => prev[key].includes(ghostId));
      const newState = { ...prev };

      if (currentActiveContainer) {
        newState[currentActiveContainer] = newState[currentActiveContainer].filter(id => id !== ghostId);
      }

      const targetItems = [...newState[overContainer]];
      const newOverIndex = targetItems.indexOf(overId);
      const insertAt = newOverIndex >= 0 ? newOverIndex : targetItems.length;

      targetItems.splice(insertAt, 0, ghostId);
      newState[overContainer] = targetItems;

      return newState;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const cardId = active.id.toString();
    const ghostId = `${cardId}-ghost`;
    setActiveId(null);
    setRotation(0);

    if (over) {
      const overContainerId = Object.keys(clonedItems).find(key => clonedItems[key].includes(ghostId));

      if (overContainerId) {
        const dropzoneIndex = parseInt(overContainerId.split('-')[1]);
        const targetIndex = clonedItems[overContainerId].indexOf(ghostId);
        animLog('BOARD', 'DRAG end → playCard', { cardId, dropzoneIndex, targetIndex });
        moves.playCard(cardId, dropzoneIndex, targetIndex);
      } else {
        animLog('BOARD', 'DRAG end (over but no ghost-container match)', { cardId, overId: over.id });
      }
    } else {
      animLog('BOARD', 'DRAG end (no drop target)', { cardId });
    }

    // Reset items to match G
    setClonedItems({
      'dz-0': G.dropzones[0].cards.map(c => c.id),
      'dz-1': G.dropzones[1].cards.map(c => c.id)
    });
  };







  if (ctx.gameover) {
    const gd = ctx.gameover;
    const locWinner: string | null = gd.locWinner ?? null;
    const locScore = gd.locScore ?? 1;
    const missionResults: Array<{ missionId: string; name: string; winner: string | null; winScore: number }> = gd.missionResults ?? [];
    const myScore = playerID === '0' ? (gd.score0 ?? 0) : (gd.score1 ?? 0);
    const oppScore = playerID === '0' ? (gd.score1 ?? 0) : (gd.score0 ?? 0);
    const locName = G.activeLocation?.name ?? 'Location';

    const nameFromMatchData = (pid: string): string => {
      const seat = matchData?.find(m => String(m.id) === pid);
      return seat?.name ?? `Player ${parseInt(pid) + 1}`;
    };
    const myName = nameFromMatchData(playerID);
    const oppName = nameFromMatchData(oppID);
    const myAvatarId = pState.avatarId ?? readAuthAvatarId();
    const oppAvatarId = oppState.avatarId ?? null;

    const isWin = gd.winner === playerID;
    const isLoss = gd.winner === oppID;

    let outcomeText = 'Stalemate';
    let outcomeClass = 'debrief-outcome-draw';

    if (gd.interrupted) {
      outcomeText = 'Match Interrupted';
      outcomeClass = 'debrief-outcome-draw';
    } else if (isWin) {
      outcomeText = 'Infiltration Success';
      outcomeClass = 'debrief-outcome-victory';
    } else if (isLoss) {
      outcomeText = 'Mission Compromised';
      outcomeClass = 'debrief-outcome-defeat';
    }

    const rows = [
      {
        label: 'Snatch points', sub: 'Turf captures during match', contested: false, winner: null as string | null,
        mine: G.players[playerID].snatchPoints, theirs: G.players[oppID].snatchPoints,
      },
      {
        label: 'Location Dominion', sub: locName, contested: true, winner: locWinner,
        mine: locWinner === playerID ? locScore : 0, theirs: locWinner === oppID ? locScore : 0,
      },
      ...missionResults.map(m => ({
        label: 'Mission Completed', sub: m.name, contested: true, winner: m.winner,
        mine: m.winner === playerID ? m.winScore : 0,
        theirs: m.winner === oppID ? m.winScore : 0,
      })),
    ];

    const isRunMatch = !!runMatchCtx.onMatchEnd;

    // Derived loot and losses from G.matchSummary
    const lootedCards: CardData[] = [];
    const lostCards: CardData[] = [];
    const myPrefix = `p${playerID}_`;
    const oppPrefix = `p${oppID}_`;

    if (G.matchSummary?.snatchEvents) {
      for (const ev of G.matchSummary.snatchEvents) {
        if (ev.snatcherID === playerID) {
          for (const c of ev.capturedCards) {
            if (c.id.startsWith(oppPrefix)) {
              lootedCards.push(c);
            }
          }
        } else if (ev.snatcherID === oppID) {
          for (const c of ev.capturedCards) {
            if (c.id.startsWith(myPrefix)) {
              lostCards.push(c);
            }
          }
        }
      }
    }

    const renderAvatarIcon = (avatarId: number | null | undefined, name: string, isWinner: boolean) => {
      const hasAvatar = avatarId !== null && avatarId !== undefined && Number.isInteger(avatarId);
      return (
        <div className={`debrief-player-avatar-wrapper ${isWinner ? 'winner-border' : ''}`}>
          {hasAvatar ? (
            <div
              className={`avatar-sprite avatar-sprite-${avatarId}`}
              style={{ width: '48px', height: '48px', margin: '0 auto', borderRadius: '50%' }}
              role="img"
              aria-label={`avatar ${avatarId}`}
            />
          ) : (
            <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#fff' }}>
              {name.trim().charAt(0).toUpperCase()}
            </span>
          )}
        </div>
      );
    };

    const renderCardPill = (c: CardData, type: 'gain' | 'loss') => {
      return (
        <span key={c.id} className={`debrief-card-pill debrief-card-pill-${type}`}>
          <span className={`suit-icon suit-icon-${c.suit.replace(/\s+/g, '-')}`} style={{ width: 14, height: 14 }} title={c.suit} />
          <span>{c.type === 'Mission' ? 'M' : c.value}</span>
        </span>
      );
    };

    const renderResourcesList = (res: Record<string, number>) => {
      const entries = Object.entries(res).filter(([, v]) => v > 0);
      if (entries.length === 0) return <span style={{ color: '#666', fontSize: 12, fontStyle: 'italic' }}>None</span>;
      return entries.map(([suit, amt]) => (
        <span key={suit} className="debrief-resource-item">
          <span className={`suit-icon suit-icon-${suit.replace(/\s+/g, '-')}`} style={{ width: 16, height: 16 }} title={suit} />
          <span style={{ fontSize: 13, fontWeight: 'bold' }}>+{amt}</span>
        </span>
      ));
    };

    return (
      <div className="gameover-screen">
        <div className="debrief-container">
          <div className="debrief-header">
            <div className="debrief-title">Operation Debrief</div>
            <div className={`debrief-outcome-badge ${outcomeClass}`}>
              {outcomeText}
            </div>
          </div>

          {/* VS & Scores Panel */}
          <div className="debrief-vs-panel">
            <div className="debrief-player-capsule">
              {renderAvatarIcon(myAvatarId, myName, myScore >= oppScore && gd.winner)}
              <span className="debrief-player-name">{myName}</span>
              <span className="debrief-player-score" style={{ color: myScore >= oppScore ? '#00ff88' : '#777' }}>
                {myScore}
              </span>
            </div>
            <div className="debrief-vs-divider">VS</div>
            <div className="debrief-player-capsule">
              {renderAvatarIcon(oppAvatarId, oppName, oppScore >= myScore && gd.winner)}
              <span className="debrief-player-name">{oppName}</span>
              <span className="debrief-player-score" style={{ color: oppScore >= myScore ? '#00ff88' : '#777' }}>
                {oppScore}
              </span>
            </div>
          </div>

          {/* Points Breakdown */}
          <div className="debrief-card">
            <div className="debrief-section-title">Points Breakdown</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              <span>Category</span>
              <div style={{ display: 'flex', gap: 24 }}>
                <span style={{ width: 60, textAlign: 'center' }}>You</span>
                <span style={{ width: 60, textAlign: 'center' }}>Opp.</span>
              </div>
            </div>
            {rows.map((r, i) => {
              const isWinnerMe = r.contested && r.winner === playerID;
              const isWinnerOpp = r.contested && r.winner === oppID;
              return (
                <div key={i} className="debrief-breakdown-row">
                  <div className="debrief-row-label">
                    <span className="debrief-row-title">{r.label}</span>
                    {r.sub && <span className="debrief-row-subtitle">{r.sub}</span>}
                    {r.contested && r.winner === null && <span className="debrief-row-subtitle" style={{ color: '#ffb703' }}>Tied</span>}
                  </div>
                  <div className="debrief-row-scores">
                    <span className="debrief-row-score-val" style={{ fontWeight: r.mine > 0 ? 700 : undefined, color: isWinnerMe ? '#00ff88' : (r.mine > 0 ? '#fff' : '#555') }}>
                      {r.contested ? (r.mine > 0 ? `+${r.mine}` : '—') : r.mine}
                    </span>
                    <span className="debrief-row-score-val" style={{ fontWeight: r.theirs > 0 ? 700 : undefined, color: isWinnerOpp ? '#00ff88' : (r.theirs > 0 ? '#fff' : '#555') }}>
                      {r.contested ? (r.theirs > 0 ? `+${r.theirs}` : '—') : r.theirs}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Loot / Stash Updates */}
          <div className="debrief-card">
            <div className="debrief-section-title">
              {isRunMatch ? 'Loot & Run Assets' : 'Loot Captured (Friendly)'}
            </div>

            <div className="debrief-loot-grid">
              <div className="debrief-loot-column">
                <span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Resources Captured</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {renderResourcesList(G.players[playerID].resources)}
                </div>
              </div>

              <div className="debrief-loot-column">
                <span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Cards Snatched</span>
                {lootedCards.length > 0 ? (
                  <div className="debrief-loot-list">
                    {lootedCards.map(c => renderCardPill(c, 'gain'))}
                  </div>
                ) : (
                  <span className="debrief-loot-empty">No cards snatched</span>
                )}
              </div>
            </div>

            {isRunMatch && (
              <div className="debrief-loot-grid" style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="debrief-loot-column">
                  <span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Cards Lost to Opponent</span>
                  {lostCards.length > 0 ? (
                    <div className="debrief-loot-list">
                      {lostCards.map(c => renderCardPill(c, 'loss'))}
                    </div>
                  ) : (
                    <span className="debrief-loot-empty" style={{ color: '#00ff88' }}>No cards lost</span>
                  )}
                </div>

                <div className="debrief-loot-column">
                  <span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Deck Sacrifices</span>
                  {isLoss && lostCards.length === 0 ? (
                    <div className="debrief-loot-empty" style={{ color: '#ff3366', fontWeight: 600 }}>
                      ⚠ 1 random card sacrificed from deck
                    </div>
                  ) : (
                    <span className="debrief-loot-empty">No sacrifice incurred</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
            <button
              className="multi-btn"
              style={{ width: '100%', maxWidth: 280 }}
              onClick={() => window.dispatchEvent(new Event('app-reset'))}
            >
              {runMatchCtx.onMatchEnd ? 'Complete Heist Node' : 'Return to Lobby'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentLocWinner = calculateLocationWinner(G);

  const nameFromMatchData = (pid: string): string => {
    const seat = matchData?.find(m => String(m.id) === pid);
    return seat?.name ?? `Player ${parseInt(pid) + 1}`;
  };
  const myName = nameFromMatchData(playerID);
  const oppName = nameFromMatchData(oppID);

  const waitingForOpponent =
    (ctx.phase === 'play' && pState.playConfirmed) ||
    (ctx.phase === 'stage' && pState.stageConfirmed);

  return (
    <CardAnimContext.Provider value={cardAnimCtx}>
    <CardInspectContext.Provider value={setInspectedCard}>
    <DragSuppressContext.Provider value={!!activeId}>
    <MotionConfig
      // Disable framer-motion layout transitions during drag so dnd-kit fully
      // owns the visuals. As soon as the drag ends, animations resume.
      transition={activeId ? { duration: 0 } : { duration: FLY_DURATION, ease: FLY_EASE }}
    >
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="board ui-scaled">
        <PlayerPanel
          side="me"
          name={myName}
          snatchPoints={myPoints}
          resources={myResources}
          avatarId={pState.avatarId ?? readAuthAvatarId()}
          syndicate={pState.syndicate}
        />
        <PlayerPanel
          side="opponent"
          name={oppName}
          snatchPoints={oppPoints}
          resources={oppResources}
          avatarId={oppState.avatarId ?? null}
          syndicate={oppState.syndicate}
        />
        <LocationBanner
          location={G.activeLocation}
          currentWinner={currentLocWinner}
          myPlayerID={playerID}
        />
        <div className="match-ticker">
          <span className="match-ticker-round">Round {G.roundCount + 1}</span>
          <span className="match-ticker-sep">·</span>
          <span className="match-ticker-phase">{ctx.phase}</span>
          {waitingForOpponent && (
            <span className="match-ticker-wait">Waiting for opponent…</span>
          )}
          {isBot && <span className="match-ticker-bot">🤖 BOT MODE</span>}
        </div>

        <div className="board-main">
          <div className="board-col-left">
            <div className="deck-title">Opp. Captures</div>
            <div ref={oppDiscardRef} className="deck-placeholder" style={{ opacity: 0.7, transform: 'scale(0.8)' }}>{oppState.discard.length}</div>

            <div style={{ marginTop: '20px' }}>
              <div className="deck-title">{G.layoutMode === 'blocks' ? 'Hand 2' : 'Draw Deck'}</div>
              <div ref={myDeckRef} className="deck-placeholder">{pState.deck.length}</div>
              {/* Disposizione a blocchi: la mano 2 l'ha scelta il giocatore nel
                  deck editor, quindi gliela mostriamo in anteprima (click =
                  dettaglio carta). La mano 2 avversaria resta nascosta. */}
              {G.layoutMode === 'blocks' && pState.deck.length > 0 && (
                <div className="next-hand-preview" title="Your Hand 2, drawn when both hands are empty">
                  {pState.deck.map(c => (
                    <button
                      key={c.id}
                      className={`next-hand-chip rarity-${c.rarity ?? 'street'}`}
                      onClick={() => setInspectedCard(c)}
                    >
                      <span className={`suit-icon suit-icon-${c.suit.replace(/\s+/g, '-')}`} />
                      <span>{c.value}</span>
                      {(c.effects?.length ?? 0) > 0 && <span className="next-hand-fx">★</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="board-col-center">
            <div className="dropzones">
              {/* 2x3 grid laid out as a cross — see Board.css:
                   row 1: [ opp-staging ][ dropzone-0 (spans 2 rows) ]
                   row 2: [ dropzone-1 (spans 2 rows) ][ dropzone-0 ]
                   row 3: [ dropzone-1                ][ my-staging ] */}
              <div className="staging-area opp-staging" style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', fontSize: '11px', color: '#888', top: '-10px', left: '10px', background: 'var(--board-color)', padding: '0 5px' }}>Opponent Staging</span>
                <AnimatePresence>
                  {oppState.stagingArea && oppState.stagingArea.length > 0
                    ? oppState.stagingArea.map(c => (
                        <Card
                          key={c.id}
                          card={c}
                          freshFromDeck={freshIds.has(c.id)}
                          containerOwnerID={oppID as '0' | '1'}
                        />
                      ))
                    : null}
                </AnimatePresence>
                {(!oppState.stagingArea || oppState.stagingArea.length === 0) && (
                  <span style={{ color: '#555', fontSize: '11px' }}>Empty</span>
                )}
              </div>

              <DroppableDropzone id="dz-0" items={clonedItems['dz-0']}>
                  {(() => {
                    const dz = G.dropzones[0];
                    const idx = 0;
                    return (
                      <>
                        <div className="dz-header">
                          Dropzone {idx + 1}
                          {dz.priorityPlayer === playerID ? " (Priority)" : ""}
                        </div>
                        <div className="mission-slot">
                          {(() => {
                            const mc = dz.missionCards[0];
                            const m = mc?.mission;
                            if (!m) return <span style={{ color: '#666' }}>No Mission</span>;
                            const missionWinner = decideWinner(
                              m.rule,
                              { discard: G.players['0'].discard, resources: G.players['0'].resources },
                              { discard: G.players['1'].discard, resources: G.players['1'].resources },
                            );
                            const missionStatus =
                              missionWinner === playerID ? { text: "You're ahead", color: '#00ff00' } :
                              missionWinner === oppID ? { text: 'Opponent ahead', color: '#ff3366' } :
                              { text: 'Tied', color: '#888' };
                            return (
                              <div>
                                <div style={{ fontWeight: 'bold', color: '#ffb703', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                  <span>{m.name}</span> <PointsBadge value={m.winScore} />
                                </div>
                                <div style={{ fontSize: '10px', color: '#bbb', fontStyle: 'italic' }}>
                                  {m.fluff}
                                </div>
                                <div style={{ fontSize: '10px', color: missionStatus.color, marginTop: '2px' }}>
                                  {missionStatus.text}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="table-cards">
                          {/* No AnimatePresence: dropzone cards transit out via
                              layoutId-FLIP (to staging on capture). Wrapping
                              with AnimatePresence delays unmount by exit
                              duration, which broke the layoutId match timing
                              and caused exitToDiscard to fire alongside the
                              new staged card's FLIP — the "pop-in" the user saw. */}
                          {(() => {
                            const dz = G.dropzones[0];
                            const myMove = G.stagedMoves[playerID];

                            // 1. Start with table cards
                            let itemsToRender = dz.cards.map(c => ({ ...c, isStaged: false, keyId: c.id }));

                            // 2. If we have a staged move here, insert it at the specific targetIndex
                            if (ctx.phase === 'play' && myMove && myMove.dropzoneIndex === 0) {
                              itemsToRender.splice(myMove.targetIndex, 0, { ...myMove.card, isStaged: true, keyId: `${myMove.card.id}-staged` });
                            }

                            // 3. If we are currently dragging to THIS dropzone, prioritize the clonedItems preview
                            // However, to keep it simple and fix the "end of list" bug, we use the display list
                            // but we must favor clonedItems if activeId is set to show the GAP.
                            if (activeId && clonedItems['dz-0'].includes(`${activeId}-ghost`)) {
                              return clonedItems['dz-0'].map(id => {
                                const actualId = id.endsWith('-ghost') ? id.replace('-ghost', '') : id;
                                const card = dz.cards.find(c => c.id === actualId) || (activeId === actualId ? activeCard : null);
                                if (!card) return null;
                                return <SortableCard key={id} card={card} isGhost={id.endsWith('-ghost')} />;
                              });
                            }

                            // Render BOTH staged and non-staged via SortableCard with the
                            // same key (c.id). When the staged-flag flips after play.onEnd,
                            // React preserves the same DOM element — no spurious initial.
                            return itemsToRender.map((c, i) => (
                              <SortableCard
                                key={c.id}
                                card={c}
                                isStaged={c.isStaged}
                                animDelay={Math.min(i * 0.08, 0.32)}
                                freshFromDeck={freshIds.has(c.id)}
                              />
                            ));
                          })()}
                        </div>
                      </>
                    );
                  })()}
              </DroppableDropzone>

              <DroppableDropzone id="dz-1" items={clonedItems['dz-1']}>
                  {(() => {
                    const dz = G.dropzones[1];
                    const idx = 1;
                    return (
                      <>
                        <div className="dz-header">
                          Dropzone {idx + 1}
                          {dz.priorityPlayer === playerID ? " (Priority)" : ""}
                        </div>
                        <div className="mission-slot">
                          {(() => {
                            const mc = dz.missionCards[0];
                            const m = mc?.mission;
                            if (!m) return <span style={{ color: '#666' }}>No Mission</span>;
                            const missionWinner = decideWinner(
                              m.rule,
                              { discard: G.players['0'].discard, resources: G.players['0'].resources },
                              { discard: G.players['1'].discard, resources: G.players['1'].resources },
                            );
                            const missionStatus =
                              missionWinner === playerID ? { text: "You're ahead", color: '#00ff00' } :
                              missionWinner === oppID ? { text: 'Opponent ahead', color: '#ff3366' } :
                              { text: 'Tied', color: '#888' };
                            return (
                              <div>
                                <div style={{ fontWeight: 'bold', color: '#ffb703', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                  <span>{m.name}</span> <PointsBadge value={m.winScore} />
                                </div>
                                <div style={{ fontSize: '10px', color: '#bbb', fontStyle: 'italic' }}>
                                  {m.fluff}
                                </div>
                                <div style={{ fontSize: '10px', color: missionStatus.color, marginTop: '2px' }}>
                                  {missionStatus.text}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="table-cards">
                          {/* No AnimatePresence — see dz-0 comment above. */}
                          {(() => {
                            const dz = G.dropzones[1];
                            const myMove = G.stagedMoves[playerID];

                            let itemsToRender = dz.cards.map(c => ({ ...c, isStaged: false, keyId: c.id }));

                            if (ctx.phase === 'play' && myMove && myMove.dropzoneIndex === 1) {
                              itemsToRender.splice(myMove.targetIndex, 0, { ...myMove.card, isStaged: true, keyId: `${myMove.card.id}-staged` });
                            }

                            if (activeId && clonedItems['dz-1'].includes(`${activeId}-ghost`)) {
                              return clonedItems['dz-1'].map(id => {
                                const actualId = id.endsWith('-ghost') ? id.replace('-ghost', '') : id;
                                const card = dz.cards.find(c => c.id === actualId) || (activeId === actualId ? activeCard : null);
                                if (!card) return null;
                                return <SortableCard key={id} card={card} isGhost={id.endsWith('-ghost')} />;
                              });
                            }

                            return itemsToRender.map((c, i) => (
                              <SortableCard
                                key={c.id}
                                card={c}
                                isStaged={c.isStaged}
                                animDelay={Math.min(i * 0.08, 0.32)}
                                freshFromDeck={freshIds.has(c.id)}
                              />
                            ));
                          })()}
                        </div>
                      </>
                    );
                  })()}
              </DroppableDropzone>

              <div className="staging-area my-staging" style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', fontSize: '11px', color: '#888', top: '-10px', left: '10px', background: 'var(--board-color)', padding: '0 5px' }}>Your Staging Area</span>
                <AnimatePresence>
                  {pState.stagingArea && pState.stagingArea.length > 0
                    ? pState.stagingArea.map(c => (
                        <Card
                          key={c.id}
                          card={c}
                          freshFromDeck={freshIds.has(c.id)}
                          containerOwnerID={playerID as '0' | '1'}
                        />
                      ))
                    : null}
                </AnimatePresence>
                {(!pState.stagingArea || pState.stagingArea.length === 0) && (
                  <span style={{ color: '#888', fontSize: '11px' }}>Empty</span>
                )}
              </div>
            </div>

            <div className="controls">
              {ctx.phase === 'play' && (
                <>
                  {!pState.playConfirmed ? (
                    <>
                      <button
                        className="multi-btn"
                        onClick={() => moves.confirmPlay()}
                        disabled={!G.stagedMoves[playerID]}
                      >
                        Confirm Play
                      </button>
                      {G.stagedMoves[playerID] && (
                        <button
                          className="multi-btn btn-secondary"
                          onClick={() => moves.undoCard()}
                        >
                          Undo
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="status-msg">Waiting for opponent (Playing)...</div>
                  )}
                </>
              )}

              {(ctx.phase === 'stage' || ctx.phase === 'sweep') && (
                <>
                  {!pState.stageConfirmed ? (
                    <button
                      className="multi-btn"
                      onClick={() => moves.confirmStage()}
                    >
                      {ctx.phase === 'sweep'
                        ? (pState.stagingArea && pState.stagingArea.length > 0 ? 'Sweep leftovers' : 'Continue')
                        : 'Collect'}
                    </button>
                  ) : (
                    <div className="status-msg">
                      {ctx.phase === 'sweep'
                        ? 'Waiting for opponent (Sweeping)...'
                        : 'Waiting for opponent (Staging)...'}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="board-col-right">
            <div className="deck-title">Opp. Draw</div>
            <div ref={oppDeckRef} className="deck-placeholder" style={{ opacity: 0.7, transform: 'scale(0.8)' }}>{oppState.deck.length}</div>

            <div style={{ marginTop: '20px' }}>
              <div className="deck-title">Your Captures</div>
              <div
                ref={myDiscardRef}
                className="deck-placeholder"
                style={{ cursor: pState.discard.length > 0 ? 'pointer' : 'default' }}
                onClick={() => { if (pState.discard.length > 0) setViewingDiscard(true); }}
                title={pState.discard.length > 0 ? 'Click to view captured cards' : ''}
              >
                {pState.discard.length}
              </div>
            </div>
          </div>
        </div>

        <div className="my-hand">
          <div className="cards-container">
            {/* No AnimatePresence here: hand cards transition out via layoutId
                FLIP (to dropzone on play, back from dropzone on undo) — they
                shouldn't run their exit animation. Without AnimatePresence,
                Card's exit prop is ignored on unmount, and framer's layoutId
                rescue catches the new mount cleanly. */}
            {myHand.map((c, i) => (
              <DraggableCard
                key={c.id}
                card={c}
                isHand
                index={i}
                total={myHand.length}
                animDelay={i * 0.1}
                freshFromDeck={freshIds.has(c.id)}
                disabled={pState.playConfirmed || ctx.phase !== 'play' || !!G.stagedMoves[playerID]}
              />
            ))}
          </div>
        </div>

      </div>

      {/* dropAnimation={null} disables dnd-kit's "snap back to source" tween
          so the DragOverlay vanishes the instant the drop fires. framer-motion's
          layoutId then animates the real card from its hand slot to the
          dropzone in one clean motion (without the "torna in mano, riflippa"
          double-animation that the default drop animation caused). */}
      <DragOverlay dropAnimation={null}>
        {activeCard ? (
          <div style={{
            transform: `rotate(${rotation}deg)`,
            transition: 'transform 0.15s ease-out',
            transformOrigin: 'center top'
          }}>
            <Card card={activeCard} isOverlay />
          </div>
        ) : null}
      </DragOverlay>

      {viewingDiscard && (
        <DiscardPopup
          cards={pState.discard}
          onClose={() => setViewingDiscard(false)}
        />
      )}

      {inspectedCard && (
        <CardDetailPopup
          card={inspectedCard}
          onClose={() => setInspectedCard(null)}
        />
      )}

      {showTutorial && (
        <TutorialOverlay
          G={G}
          phase={ctx.phase}
          gameover={ctx.gameover}
          onDismiss={() => setTutorialDismissed(true)}
        />
      )}

      {/* Snatch full screen overlay notification */}
      <AnimatePresence>
        {snatchNotification && (
          <motion.div
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.5, y: -50 }}
            transition={{ type: 'spring', damping: 15, stiffness: 200 }}
            style={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              pointerEvents: 'none',
            }}
          >
            {/* Dark background flash */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'absolute',
                inset: 0,
                background: '#000',
              }}
            />

            {/* Glowing Panel */}
            <motion.div
              initial={{ y: 50 }}
              animate={{ y: 0 }}
              exit={{ y: -50 }}
              style={{
                position: 'relative',
                background: 'linear-gradient(135deg, rgba(16, 16, 28, 0.95), rgba(8, 8, 12, 0.98))',
                border: `3px solid ${
                  snatchNotification.isBoth ? '#ffb703' : snatchNotification.isMe ? '#00ff88' : '#ff3366'
                }`,
                borderRadius: '16px',
                padding: '30px 60px',
                boxShadow: `0 10px 40px rgba(0,0,0,0.8), 0 0 30px ${
                  snatchNotification.isBoth ? 'rgba(255, 183, 3, 0.3)' : snatchNotification.isMe ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 51, 102, 0.3)'
                }`,
                textAlign: 'center',
              }}
            >
              <h1
                style={{
                  margin: '0 0 10px 0',
                  fontSize: '48px',
                  fontWeight: 900,
                  letterSpacing: '4px',
                  color: snatchNotification.isBoth ? '#ffb703' : snatchNotification.isMe ? '#00ff88' : '#ff3366',
                  textShadow: `0 0 20px ${
                    snatchNotification.isBoth ? 'rgba(255, 183, 3, 0.6)' : snatchNotification.isMe ? 'rgba(0, 255, 136, 0.6)' : 'rgba(255, 51, 102, 0.6)'
                  }`,
                }}
              >
                {snatchNotification.text}
              </h1>
              <p style={{ margin: 0, color: '#ccc', fontSize: '16px', fontWeight: 600 }}>
                {snatchNotification.isBoth
                  ? 'Both dropzones cleared simultaneously! (+1 SP each)'
                  : snatchNotification.isMe
                  ? 'Your gang cleared the street! (+1 Snatch Point)'
                  : 'Opponent cleaned out the street! (+1 Snatch Point)'}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DndContext>
    </MotionConfig>
    </DragSuppressContext.Provider>
    </CardInspectContext.Provider>
    </CardAnimContext.Provider>
  );
}
